import { useEffect, useState } from 'react'

/**
 * Tiny hash-based client router — no dependency.
 *
 * The active view lives in `window.location.hash` (e.g. `#/concepts`). This
 * keeps deep-links and the browser back/forward buttons working with zero
 * routing library. Five fixed views; unknown hashes fall back to the default.
 */

export const ROUTES = [
  'inbox',
  'concepts',
  'poses',
  'references',
  'shoots',
  'lighting',
  'locations',
  'people',
  'search',
] as const

export type Route = (typeof ROUTES)[number]

/** Default view when the hash is empty or unrecognized (SPEC: references-first). */
export const DEFAULT_ROUTE: Route = 'references'

function isRoute(value: string): value is Route {
  return (ROUTES as ReadonlyArray<string>).includes(value)
}

/** Parse the current `window.location.hash` into a known Route. */
export function parseHash(hash: string): Route {
  // Accept "#/concepts", "#concepts", "/concepts", "concepts".
  const cleaned = hash.replace(/^#/, '').replace(/^\//, '').trim().toLowerCase()
  return isRoute(cleaned) ? cleaned : DEFAULT_ROUTE
}

/** Build the canonical hash href for a route (use in <a> for accessible links). */
export function routeHref(route: Route): string {
  return `#/${route}`
}

/** Imperatively navigate to a route by updating the hash. */
export function navigate(route: Route): void {
  if (typeof window === 'undefined') return
  const next = routeHref(route)
  if (window.location.hash !== next) {
    window.location.hash = next
  }
}

/**
 * Subscribe to the current route. Re-renders on `hashchange` and normalizes the
 * URL to the canonical form on first mount (so an empty hash becomes
 * `#/references`).
 */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    typeof window === 'undefined'
      ? DEFAULT_ROUTE
      : parseHash(window.location.hash),
  )

  useEffect(() => {
    const sync = () => setRoute(parseHash(window.location.hash))

    // Normalize the initial URL so the hash always reflects the resolved route.
    if (window.location.hash === '' || parseHash(window.location.hash) !== route) {
      navigate(parseHash(window.location.hash))
    }

    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
    // Run once on mount; `sync` reads live state from the event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return route
}
